import { LoadingStateModel } from '@/models/loadingState';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useStudyStore } from '@/stores/study';
import type { EntryModel, PostEntry, CaseModel, PostLockUnlockResource } from '@/models';
import EntryService from '@/services/entryService';
import { TableColumnType, type TableColumnModel } from '@/components/common/table/interfaces/TableColumnModel';
import type { PaginatorData } from '@/components/common/interfaces/PaginatorData';
import StudyService from '@/services/studyService';
import { DateTime } from 'luxon';
import { DATA_VIEW_FILTER, LocalStorageUtils } from '@/utils';
import { useFormStore } from '@/stores/forms';
import { useCaseStore } from '@/stores/case';

export const useEntryListStore = defineStore('entryList', () => {

  const tableColumns = ref<TableColumnModel[]>([
    {
      id: 1,
      header: 'Title',
      key: 'formTitle',
      type: TableColumnType.TEXT_WTIH_ICON,
      icon: {
        icon: 'file',
        style: 'far',
        color: '#272a2e'
      },
      sortable: true
    },
    {
      id: 3,
      header: 'Creation Date',
      type: TableColumnType.TEXT,
      key: 'createdAt',
      sortable: true
    },
    {
      id: 4,
      header: 'Last Edited On',
      type: TableColumnType.TEXT,
      key: 'updatedAt',
      sortable: true
    },
    {
      id: 5,
      header: 'Last Edited By',
      type: TableColumnType.USER,
      key: 'creatorNames',
      sortable: true
    },
    {
      id: 6,
      header: '',
      type: TableColumnType.SQAURE_BUTTON_WITH_ICON,
      key: '',
      sortable: false
    }
  ]);

  const selectedCase = ref<CaseModel>();

  const formStore = useFormStore();
  const caseStore = useCaseStore();
  const studyStore = useStudyStore();
  const entryList = ref<EntryModel[]>([]);
  const totalEntriesCount = ref<number>(0);
  const currentPage = ref(1);
  const pageLimit = ref(25);
  const sortField = ref<string | undefined>('createdAt');
  const sortOrder = ref<string | undefined>('DESC');

  const entriesLoader = ref(new LoadingStateModel({ isLoading: false }));
  const getEntries = async () => {
    entriesLoader.value.startLoading();
    const caseColumnIndex = tableColumns.value.findIndex(column => column.id === 2);
    if (caseColumnIndex > -1) {
      tableColumns.value.splice(caseColumnIndex, 1);
    }
    try {
      let entryResponse;
      if (selectedCase.value?.archivedAt) {
        entryResponse = await EntryService.getCaseArchivedEntries(
            studyStore.selectedStudyId!, selectedCase.value!.identifier);
      } else if (selectedCase.value) {
        entryResponse = await EntryService.getCaseEntries(
            studyStore.selectedStudyId!, selectedCase.value!.identifier, currentPage.value,
            pageLimit.value, sortField.value, sortOrder.value
        );
      }
      entryList.value = entryResponse?.entries ?? [];
      totalEntriesCount.value = entryResponse?.entriesCount ?? 0;
      getForms();
    } catch (error) {
      entriesLoader.value.setError(error);
    } finally {
      entriesLoader.value.stopLoadingAndFinish();
    }
  };

  const getForms = async () => {
    await formStore.getStudyForms();

    for (let i = 0; i < formStore.formList.length; i++) {
      const form = formStore.formList[i];
      if (form.enforcements.enforceOrder) {
        if (entryList.value.length == 0) {
          formStore.formList.forEach((formItem, index) => {
            if (index > i) {
              formItem.enforcements.enforceOrder = true;
            }
          });
        }
        break;
      }
    }
  };

  const getStudyEntries = async () => {
    entriesLoader.value.startLoading();
    if (studyStore.selectedStudy?.longitudinal === true) {
      const caseColumnIndex = tableColumns.value.findIndex(column => column.id === 2);
      if (caseColumnIndex === -1) {
        const caseTableColumn = {
          id: 2,
          header: 'Case',
          type: TableColumnType.SELECT,
          key: 'caseItem',
          sortable: false
        };
        tableColumns.value.splice(1, 0, caseTableColumn);
      }
    } else {
      const caseColumnIndex = tableColumns.value.findIndex(column => column.id === 2);
      if (caseColumnIndex > -1) {
        tableColumns.value.splice(caseColumnIndex, 1);
      }
    }
    try {
      if (formStore.formList.length === 0) {
        await formStore.getStudyForms();
      }
      if (caseStore.studyCaseList.length === 0) {
        await caseStore.getStudyCases();
      }
      const entryResponse = await EntryService.getStudyEntries(
        caseStore.studyCaseList, studyStore.selectedStudyId!, currentPage.value,
        pageLimit.value, sortField.value, sortOrder.value
      );
      entryList.value = entryResponse.entries;
      totalEntriesCount.value = entryResponse.entriesCount;
    } catch (error) {
      entriesLoader.value.setError(error);
    } finally {
      entriesLoader.value.stopLoadingAndFinish();
    }
  };

  const onPageChange = (pageData: PaginatorData) => {
    currentPage.value = pageData.page + 1;
    pageLimit.value = pageData.rows;
    if (LocalStorageUtils.getCurrentDataViewFilter() === DATA_VIEW_FILTER.ENTRIES && !selectedCase.value) {
      getStudyEntries();
    } else {
      getEntries();
    }
  };

  const setSort = (sortBy?: string) => {
    if (sortBy != sortField.value) {
      sortField.value = sortBy;
      if (LocalStorageUtils.getCurrentDataViewFilter() === DATA_VIEW_FILTER.ENTRIES && !selectedCase.value) {
        getStudyEntries();
      } else {
        getEntries();
      }
    }
  };

  const setSortOrder = (order?: number) => {
    if (order != sortOrder.value && order !== null) {
      sortOrder.value = order === 1 ? 'ASC' : 'DESC';
      if (LocalStorageUtils.getCurrentDataViewFilter() === DATA_VIEW_FILTER.ENTRIES && !selectedCase.value) {
        getStudyEntries();
      } else {
        getEntries();
      }
    }
  };

  const entryActionLoader = ref(new LoadingStateModel({ isLoading: false }));
  const lockUnlockEntries = async (selectedEntries: EntryModel[], status: string) => {
    entryActionLoader.value.startLoading();
    const postPayload: PostLockUnlockResource = {
      responses: selectedEntries.map(entryItem => ({
        identifier: entryItem.identifier,
        revisionIdentifier: entryItem.identifier
      })),
      status
    };
    try {
      if (status === 'locked' || status === 'unlocked') {
        await StudyService.lockUnlockResources(studyStore.selectedStudyId!, postPayload);
        entryActionLoader.value.setSuccess(`Selected entries have been ${status}`);
      } else {
        await StudyService.archiveResources(studyStore.selectedStudyId!, postPayload);
        entryActionLoader.value.setSuccess(`Selected entries have been archived`);
      }
      if (LocalStorageUtils.getCurrentDataViewFilter() === DATA_VIEW_FILTER.ENTRIES && !selectedCase.value) {
        getStudyEntries();
      } else {
        getEntries();
      }
    } catch (error) {
      entryActionLoader.value.setError(error);
    } finally {
      entryActionLoader.value.stopLoadingAndFinish();
    }
  };

  const updateEntryCase = async (entryId: string, caseId?: string) => {
    entriesLoader.value.startLoading();
    try {
      if (formStore.formList.length > 0) {
        const payload: PostEntry = {
          caseUri: caseId ? `case/${caseId}` : null,
          revisionDate: DateTime.local().toSeconds()
        };
        await EntryService.updateEntry(entryId, payload);
        getStudyEntries();
      }
    } catch (error) {
      entriesLoader.value.setError(error);
    } finally {
      entriesLoader.value.stopLoadingAndFinish();
    }
  };

  const resetData = () => {
    selectedCase.value = undefined;
    entryList.value = [];
  };

  const resetEntryList = (caseModel?: CaseModel) => {
    entryList.value = [];
    selectedCase.value = caseModel;
    currentPage.value = 1;
    pageLimit.value = 25;
    sortField.value = 'createdAt';
  };

  const cloneEntry = async (entry: EntryModel) => {
    entriesLoader.value.startLoading();
    try {
      await EntryService.cloneEntry(entry.identifier);
    } catch (error) {
      entriesLoader.value.setError(error);
    } finally {
      entriesLoader.value.stopLoadingAndFinish();
    }
  };

  return {
    getEntries,
    getStudyEntries,
    onPageChange,
    lockUnlockEntries,
    updateEntryCase,
    resetData,
    resetEntryList,
    setSort,
    setSortOrder,
    cloneEntry,
    currentPage,
    tableColumns,
    selectedCase,
    entriesLoader,
    entryActionLoader,
    entryList,
    totalEntriesCount
  };
});
